import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Grid, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography
} from "@material-ui/core";
import { Add, DeleteOutline, EditOutlined, Publish, Refresh } from "@material-ui/icons";
import { Alert, Autocomplete, Pagination } from "@material-ui/lab";
import PageContainer from "@jumbo/components/PageComponents/layouts/PageContainer";
import axios from "services/auth/jwt/config";
import { getMyAccess } from "services/adminAccess";
import reportCardService from "services/reportCard";
import resultService from "services/reportCardResult";

const errorMessage = error =>
  (error.response && error.response.data && error.response.data.msgFa) ||
  "انجام عملیات ممکن نشد. لطفاً دوباره تلاش کنید.";
const emptyDraft = () => ({ owner: "", transcripts: [{ title: "", result: "" }] });
const sampleCsvUrl = `${process.env.PUBLIC_URL}/samples/reportcard-results-sample.csv`;

export default function ReportCardResults() {
  const { id } = useParams();
  const { search } = useLocation();
  const owner = new URLSearchParams(search).get("owner") || "";
  const editRequested = Boolean(owner) && new URLSearchParams(search).get("edit") === "1";
  return <Results key={`${id}:${owner}:${editRequested}`} id={id} owner={owner} editRequested={editRequested} />;
}

function Results({ id, owner, editRequested }) {
  const editHandled = useRef(false);
  const [access, setAccess] = useState(null);
  const [card, setCard] = useState(null);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchString, setSearchString] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editor, setEditor] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [editorError, setEditorError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importError, setImportError] = useState("");
  const [importErrors, setImportErrors] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearchString(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!editor) return;
    let active = true;
    setUsersLoading(true);
    setUsersError("");
    setUserOptions([]);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get("/user/getallusers", {
          params: { page: 1, limit: 99, searchString: userSearch }
        });
        if (active) setUserOptions(data.docs || []);
      } catch (err) {
        if (active) setUsersError(errorMessage(err));
      } finally {
        if (active) setUsersLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [editor, userSearch]);

  useEffect(() => {
    setPage(1);
    setEditor(null);
    setDeleteTarget(null);
    setSuccess("");
    setImportOpen(false);
    setCsvFile(null);
  }, [id]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const permission = await getMyAccess("ReportCard");
        if (!active) return;
        setAccess(permission);
        if (!permission) return;
        const [cardResponse, resultResponse] = await Promise.all([
          reportCardService.getById(id), resultService.getAll(id, page, searchString, owner)
        ]);
        if (!active) return;
        const data = resultResponse.data;
        setCard(cardResponse.data);
        if (page > Math.max(1, data.totalPages)) {
          setPage(Math.max(1, data.totalPages));
          return;
        }
        setRecords(data.docs);
        setTotalPages(Math.max(1, data.totalPages));
        setTotalDocs(data.totalDocs);
      } catch (err) {
        if (active) setError(errorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [id, page, searchString, owner, refresh]);

  const openCreate = () => {
    setDraft(emptyDraft());
    setSelectedUser(null);
    setUserSearch("");
    setEditorError("");
    setSuccess("");
    setEditor({ id: null });
  };
  const openEdit = useCallback(async recordId => {
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await resultService.getById(recordId);
      const ownerId = (data.owner && (data.owner._id || data.owner)) || "";
      const listedRecord = records.find(record => record._id === recordId);
      const owner = data.owner && typeof data.owner === "object" ? data.owner :
        listedRecord && listedRecord.owner && listedRecord.owner._id === ownerId ? listedRecord.owner : null;
      setSelectedUser(owner || (ownerId ? { _id: ownerId } : null));
      setUserSearch("");
      setDraft({
        owner: ownerId,
        transcripts: (data.transcripts || []).map(item => ({ ...item }))
      });
      setEditorError("");
      setEditor({ id: recordId });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [records]);
  useEffect(() => {
    if (!editRequested || editHandled.current || loading || !access || error) return;
    editHandled.current = true;
    if (totalDocs === 1 && records.length === 1) openEdit(records[0]._id);
  }, [editRequested, loading, access, error, totalDocs, records, openEdit]);
  const changeTranscript = (index, field, value) => setDraft(current => ({
    ...current,
    transcripts: current.transcripts.map((item, i) => i === index ? { ...item, [field]: value } : item)
  }));
  const save = async event => {
    event.preventDefault();
    if (busy) return;
    if (!/^[a-f\d]{24}$/i.test(draft.owner.trim())) {
      setEditorError("کاربر مورد نظر را از فهرست انتخاب کنید.");
      return;
    }
    if (draft.transcripts.some(item => !item.title.trim() || !item.result.trim())) {
      setEditorError("عنوان و نتیجه تمام ردیف‌ها را وارد کنید.");
      return;
    }
    setBusy(true);
    setEditorError("");
    const payload = {
      reportCard: id,
      owner: draft.owner.trim(),
      transcripts: draft.transcripts.map(item => ({
        ...(item._id ? { _id: item._id } : {}),
        title: item.title.trim(), result: item.result.trim()
      }))
    };
    try {
      if (editor.id) await resultService.update(editor.id, payload);
      else await resultService.create(payload);
      setEditor(null);
      setSuccess("نتیجه با موفقیت ذخیره شد.");
      if (!editor.id) setPage(1);
      setRefresh(value => value + 1);
    } catch (err) {
      setEditorError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true);
    setDeleteError("");
    try {
      await resultService.remove(deleteTarget._id);
      setDeleteTarget(null);
      setSuccess("نتیجه با موفقیت حذف شد.");
      if (records.length === 1 && page > 1) setPage(page - 1);
      else setRefresh(value => value + 1);
    } catch (err) {
      setDeleteError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const importCsv = async event => {
    event.preventDefault();
    if (busy || !csvFile) return;
    setBusy(true);
    setImportError("");
    setImportErrors([]);
    try {
      const { data } = await resultService.importCsv(id, csvFile);
      setImportOpen(false);
      setCsvFile(null);
      setSuccess(`${data.importedRows} ردیف نتیجه برای ${data.importedUsers} کاربر با موفقیت ثبت شد.`);
      setPage(1);
      setRefresh(value => value + 1);
    } catch (err) {
      setImportError(errorMessage(err));
      const response = err.response && err.response.data;
      setImportErrors(response && Array.isArray(response.errors) ? response.errors : []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContainer heading="نتایج کارنامه" breadcrumbs={[
      { label: "خانه", link: "/" },
      { label: "مدیر کارنامه", link: "/reportcards" },
      { label: "نتایج", isActive: true }
    ]}>
      <Box dir="rtl">
        <Box mb={2}><Button component={Link} to="/reportcards">بازگشت به کارنامه‌ها</Button></Box>
        {access && owner && <Box mb={2}><Alert severity="info" action={
          <Button component={Link} to={`/reportcards/${id}/results`} color="inherit">نمایش همه نتایج</Button>
        }>نتایج این کارنامه فقط برای کاربر انتخاب‌شده نمایش داده می‌شود.</Alert></Box>}
        {editRequested && !loading && totalDocs > 1 && <Box mb={2}><Alert severity="info">
          این کاربر چند نتیجه دارد. نتیجه مورد نظر را برای ویرایش انتخاب کنید.
        </Alert></Box>}
        {success && <Box mb={2}><Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert></Box>}
        {error && <Box mb={2}><Alert severity="error" action={
          <Button color="inherit" onClick={() => setRefresh(value => value + 1)}>تلاش مجدد</Button>
        }>{error}</Alert></Box>}
        {access === false && <Alert severity="warning">شما به بخش مدیر کارنامه دسترسی ندارید.</Alert>}
        {access && <Box mb={2}>
          <TextField id="result-owner-search" fullWidth size="small" variant="outlined"
            label="جستجو با نام، کد ملی یا کد پرسنلی" type="search"
            value={searchInput} disabled={busy} InputLabelProps={{ shrink: true }}
            onChange={event => setSearchInput(event.target.value)} />
        </Box>}
        {loading ? <Box p={5} textAlign="center"><CircularProgress aria-label="در حال دریافت نتایج" /></Box> : access && !error && <>
          <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h3">نتایج {card && card.title}</Typography>
            <Box my={1}>
              <Button startIcon={<Refresh />} disabled={busy} onClick={() => setRefresh(value => value + 1)}>بازخوانی</Button>
              <Button color="primary" startIcon={<Publish />} disabled={busy} onClick={() => {
                setCsvFile(null);
                setImportError("");
                setImportErrors([]);
                setSuccess("");
                setImportOpen(true);
              }}>ورود از CSV</Button>
              <Button variant="contained" color="primary" startIcon={<Add />} disabled={busy} onClick={openCreate}>افزودن نتیجه</Button>
            </Box>
          </Box>
          <Paper>
            <TableContainer><Table aria-label="نتایج کارنامه">
              <TableHead><TableRow>
                <TableCell>کاربر</TableCell><TableCell>کد پرسنلی</TableCell><TableCell>کد ملی</TableCell><TableCell>عملیات</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {records.length === 0 ? <TableRow><TableCell colSpan={4} align="center"><Box py={5}>{searchString || owner ? "نتیجه‌ای با این مشخصات یافت نشد." : "هنوز نتیجه‌ای برای این کارنامه ثبت نشده است."}</Box></TableCell></TableRow> : records.map(record => (
                  <TableRow key={record._id} hover>
                    <TableCell>{record.owner ? record.owner.fullName || record.owner._id || record.owner : "کاربر حذف‌شده"}</TableCell>
                    <TableCell>{(record.owner && record.owner.personnelCode) || "—"}</TableCell>
                    <TableCell>{(record.owner && record.owner.nationalCode) || "—"}</TableCell>
                    <TableCell><Box display="flex">
                      <Button color="primary" startIcon={<EditOutlined />} disabled={busy} onClick={() => openEdit(record._id)}>ویرایش</Button>
                      <Button color="secondary" startIcon={<DeleteOutline />} disabled={busy} onClick={() => { setDeleteError(""); setDeleteTarget(record); }}>حذف</Button>
                    </Box></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
            <Box p={2} display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">{totalDocs} نتیجه</Typography>
              <Pagination count={totalPages} page={page} disabled={busy} color="primary" onChange={(_, value) => setPage(value)}
                getItemAriaLabel={(type, number) => type === "page" ? `صفحه ${number}` : type === "next" ? "صفحه بعد" : "صفحه قبل"} />
            </Box>
          </Paper>
        </>}
        <Dialog open={importOpen} onClose={() => { if (!busy) setImportOpen(false); }} fullWidth maxWidth="md" aria-labelledby="result-import-title">
          <form onSubmit={importCsv} dir="rtl">
            <DialogTitle id="result-import-title">ورود نتایج از CSV</DialogTitle>
            <DialogContent dividers>
              <Typography paragraph>فایل CSV UTF-8 انتخاب کنید. ستون اول باید «کد ملی» باشد. عنوان ستون‌های بعدی در سطر اول، عنوان نتایج است و هر ردیف بعدی شامل کد ملی کاربر و نتایج او در همان ستون‌ها است. حداقل یک ستون نتیجه لازم است و عنوان ستون‌ها و نتیجه‌ها نباید خالی باشند.</Typography>
              <Typography paragraph>ردیف‌های دارای کد ملی یکسان در یک نتیجه جمع می‌شوند. برای هر کاربر یک نتیجه جدید ساخته می‌شود و نتایج قبلی جایگزین نمی‌شوند.</Typography>
              <Typography paragraph>کد ملی باید با مقدار ثبت‌شده برای کاربر مطابقت داشته باشد. برای حفظ صفرهای ابتدایی، ستون کد ملی را در اکسل به صورت متن (Text) تنظیم کنید. حداکثر حجم فایل ۲ مگابایت و حداکثر تعداد ردیف‌ها ۵۰۰۰ است.</Typography>
              <Box mb={2}><Alert severity="info">تمام ردیف‌ها با هم ثبت می‌شوند. اگر حتی یک کد ملی به کاربر یکتا مرتبط نشود یا ردیفی نامعتبر باشد، هیچ نتیجه‌ای ثبت نخواهد شد.</Alert></Box>
              <Box mb={2}><Button component="a" color="primary" download="reportcard-results-sample.csv" href={sampleCsvUrl}>
                دریافت نمونه CSV
              </Button></Box>
              <Box mb={2}><TextField type="file" fullWidth variant="outlined" label="فایل CSV" InputLabelProps={{ shrink: true }}
                disabled={busy} inputProps={{ accept: ".csv,text/csv" }} onChange={event => {
                  const file = event.target.files && event.target.files[0];
                  setImportError("");
                  setImportErrors([]);
                  setCsvFile(null);
                  if (!file) return;
                  if (!/\.csv$/i.test(file.name) || !file.size || file.size > 2 * 1024 * 1024) {
                    setImportError("یک فایل CSV غیرخالی با حجم حداکثر ۲ مگابایت انتخاب کنید.");
                    return;
                  }
                  setCsvFile(file);
                }} /></Box>
              {importError && <Box mb={2}><Alert severity="error">{importError}</Alert></Box>}
              {importErrors.length > 0 && <TableContainer style={{ maxHeight: 300 }}><Table size="small" stickyHeader aria-label="خطاهای فایل CSV">
                <TableHead><TableRow><TableCell>سطر فایل</TableCell><TableCell>کد ملی</TableCell><TableCell>خطا</TableCell></TableRow></TableHead>
                <TableBody>{importErrors.map((item, index) => <TableRow key={index}>
                  <TableCell>{item.row}</TableCell><TableCell>{item.nationalCode || "—"}</TableCell><TableCell>{item.message}</TableCell>
                </TableRow>)}</TableBody>
              </Table></TableContainer>}
            </DialogContent>
            <DialogActions>
              <Button disabled={busy} onClick={() => setImportOpen(false)}>انصراف</Button>
              <Button type="submit" color="primary" variant="contained" disabled={busy || !csvFile}>
                {busy ? "در حال بررسی و ثبت…" : "ثبت نتایج فایل"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <Dialog open={Boolean(editor)} onClose={() => { if (!busy) setEditor(null); }} fullWidth maxWidth="md" aria-labelledby="result-editor-title">
          <form onSubmit={save} dir="rtl">
            <DialogTitle id="result-editor-title">{editor && editor.id ? "ویرایش نتیجه" : "افزودن نتیجه"}</DialogTitle>
            <DialogContent dividers>
              {editorError && <Box mb={2}><Alert severity="error">{editorError}</Alert></Box>}
              <Box mb={3}>
                <Autocomplete
                  id="result-owner"
                  value={selectedUser}
                  options={selectedUser && !userOptions.some(user => user._id === selectedUser._id)
                    ? [selectedUser, ...userOptions] : userOptions}
                  getOptionLabel={user => [user.fullName || user._id, user.nationalCode, user.personnelCode].filter(Boolean).join(" — ")}
                  getOptionSelected={(option, value) => option._id === value._id}
                  filterOptions={options => options}
                  disabled={busy}
                  loading={usersLoading}
                  loadingText="در حال دریافت کاربران…"
                  noOptionsText={usersError || "کاربری یافت نشد."}
                  clearText="پاک کردن"
                  openText="نمایش کاربران"
                  closeText="بستن"
                  onInputChange={(_, value, reason) => {
                    if (reason === "input" || reason === "clear") setUserSearch(value);
                  }}
                  onChange={(_, user) => {
                    setSelectedUser(user);
                    setDraft(current => ({ ...current, owner: user ? user._id : "" }));
                  }}
                  renderInput={params => <TextField {...params} label="کاربر" required fullWidth autoFocus variant="outlined"
                    error={Boolean(usersError)}
                    helperText={usersError || "جستجو با نام، کد ملی یا کد پرسنلی و انتخاب کاربر از فهرست"}
                    InputProps={{ ...params.InputProps, endAdornment: <>
                      {usersLoading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </> }} />}
                />
              </Box>
              <Typography variant="h4" gutterBottom>ردیف‌های نتیجه</Typography>
              {draft.transcripts.map((item, index) => <Box key={index} mb={2}><Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}><TextField label="عنوان" required fullWidth variant="outlined" value={item.title} disabled={busy}
                  onChange={event => changeTranscript(index, "title", event.target.value)} /></Grid>
                <Grid item xs={12} sm={6}><TextField label="نتیجه" required fullWidth multiline variant="outlined" value={item.result} disabled={busy}
                  onChange={event => changeTranscript(index, "result", event.target.value)} /></Grid>
                <Grid item xs={12} sm={2}><Button color="secondary" disabled={busy} aria-label={`حذف ردیف ${index + 1}`}
                  onClick={() => setDraft(current => ({ ...current, transcripts: current.transcripts.filter((_, i) => i !== index) }))}>حذف ردیف</Button></Grid>
              </Grid></Box>)}
              <Button color="primary" startIcon={<Add />} disabled={busy} onClick={() => setDraft(current => ({
                ...current, transcripts: [...current.transcripts, { title: "", result: "" }]
              }))}>افزودن ردیف</Button>
            </DialogContent>
            <DialogActions>
              <Button disabled={busy} onClick={() => setEditor(null)}>انصراف</Button>
              <Button type="submit" variant="contained" color="primary" disabled={busy}>{busy ? "در حال ذخیره…" : "ذخیره نتیجه"}</Button>
            </DialogActions>
          </form>
        </Dialog>
        <Dialog open={Boolean(deleteTarget)} onClose={() => { if (!busy) setDeleteTarget(null); }} dir="rtl" aria-labelledby="result-delete-title">
          <DialogTitle id="result-delete-title">حذف نتیجه</DialogTitle>
          <DialogContent>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <DialogContentText>آیا از حذف این نتیجه مطمئن هستید؟ این عملیات قابل بازگشت نیست.</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button autoFocus disabled={busy} onClick={() => setDeleteTarget(null)}>انصراف</Button>
            <Button color="secondary" disabled={busy} onClick={remove}>{busy ? "در حال حذف…" : "حذف نتیجه"}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageContainer>
  );
}
